import {
  Actor,
  Buff,
  Falling,
  Pickup,
  PlayerTag,
  Renderable,
  Transform,
  type PickupKind,
} from '@/components'
import type { Entity } from '@/core/ecs/Entity'
import type { SystemFn } from '@/core/ecs/Scheduler'
import type { World } from '@/core/ecs/World'
import { ArenaResource } from '@/game/arenas/Arena'
import { Events } from '@/game/events'
import { EventsResource, MatchResource, RngResource } from '@/game/resources'
import { SceneBridgeResource } from '@/gfx/SceneBridge'

/** Seconds between spawn attempts, and how many may exist at once. */
const SPAWN_INTERVAL = 11
const MAX_ACTIVE = 2
/** The first one arrives a few seconds in, not the instant the round starts. */
const FIRST_SPAWN = 7

/**
 * Five seconds of true knockback immunity — the low end of the requested
 * range, because it is an absolute effect: nothing moves you, so the holder
 * simply walks in and throws. It stays absolute (that is the point of picking
 * it up) but instability still accumulates underneath, so the moment it drops
 * the holder is the most fragile thing on the platform. Taking the shield is a
 * loan, not a gift.
 */
export const SHIELD_DURATION = 5
export const HASTE_DURATION = 8
export const HASTE_SPEED = 1.55
export const HASTE_ACCEL = 1.4

const KINDS: PickupKind[] = ['shield', 'haste']

/**
 * Timed pickups: a short window of immunity or of speed.
 *
 * They exist to break stalemates. Two players circling each other on a shrinking
 * platform is the least interesting state this game can be in, and a shield
 * appearing between them forces someone to commit to reaching it.
 *
 * Both effects are loud, brief and visible on the character, so the other player
 * always knows what they are up against.
 */
export function createPickupSystem(): SystemFn {
  // Closure state rather than module state: a second World must not inherit a
  // spawn timer from the first one.
  let spawnTimer = FIRST_SPAWN
  let lastRound = -1

  return ({ world, dt }) => {
    const match = world.resource(MatchResource)

    tickBuffs(world, dt)
    animate(world, dt)
    if (match.phase !== 'fight') return

    if (match.roundIndex !== lastRound) {
      lastRound = match.roundIndex
      spawnTimer = FIRST_SPAWN
    }

    spawnTimer -= dt
    if (spawnTimer <= 0) {
      spawnTimer = SPAWN_INTERVAL
      trySpawn(world)
    }

    collect(world)
  }
}

function tickBuffs(world: World, dt: number): void {
  for (const e of world.query([Buff, Actor]).entities()) {
    const buff = world.read(e, Buff)
    const actor = world.read(e, Actor)

    if (buff.shield > 0) buff.shield = Math.max(0, buff.shield - dt)
    if (buff.haste > 0) buff.haste = Math.max(0, buff.haste - dt)

    // Haste is applied by rewriting the actor's own limits, so every other
    // system — movement, dash, the speed cap — inherits it for free. Scaled
    // from the actor's own base values rather than from literals, or collecting
    // a pickup would silently reset any other tuning of that actor.
    actor.maxSpeed = buff.haste > 0 ? actor.baseSpeed * HASTE_SPEED : actor.baseSpeed
    actor.accel = buff.haste > 0 ? actor.baseAccel * HASTE_ACCEL : actor.baseAccel

    if (buff.shield <= 0 && buff.haste <= 0) world.remove(e, Buff)
  }
}

function animate(world: World, dt: number): void {
  for (const e of world.query([Pickup]).entities()) {
    world.read(e, Pickup).age += dt
  }
}

function trySpawn(world: World): void {
  if (world.query([Pickup]).count >= MAX_ACTIVE) return
  const spot = findSpot(world)
  if (!spot) return
  const rng = world.resource(RngResource)
  spawnPickup(world, rng.pick(KINDS), spot.x, spot.z)
}

/**
 * Somewhere inside the platform, away from the rim and from both fighters —
 * a pickup that spawns on top of someone is a coin flip, not a decision.
 */
function findSpot(world: World): { x: number; z: number } | null {
  const arena = world.resource(ArenaResource)
  const rng = world.resource(RngResource)
  const players: Array<{ x: number; z: number }> = []
  for (const e of world.query([PlayerTag, Transform]).entities()) {
    const tr = world.read(e, Transform)
    players.push({ x: tr.x, z: tr.z })
  }

  let best: { x: number; z: number; unfairness: number } | null = null

  for (let attempt = 0; attempt < 40; attempt++) {
    const x = arena.def.centerX + rng.spread(arena.def.extentX)
    const z = arena.def.centerZ + rng.spread(arena.def.extentZ)
    if (arena.distance(x, z) > -2.5) continue
    if (players.some((p) => Math.hypot(p.x - x, p.z - z) < 5)) continue

    // Prefer spots that are a fair race. A purely random point routinely landed
    // several metres closer to one fighter, which turned every pickup into a
    // coin flip decided before either player reacted.
    const unfairness =
      players.length === 2
        ? Math.abs(
            Math.hypot(players[0].x - x, players[0].z - z) -
              Math.hypot(players[1].x - x, players[1].z - z),
          )
        : 0
    if (!best || unfairness < best.unfairness) best = { x, z, unfairness }
    if (unfairness < 1.5) break
  }

  return best ? { x: best.x, z: best.z } : null
}

export function spawnPickup(
  world: World,
  kind: PickupKind,
  x: number,
  z: number,
): Entity {
  const gfx = world.tryResource(SceneBridgeResource)
  const e = world.createEntity()
  world.add(e, Transform, { x, z, prevX: x, prevZ: z })
  world.add(e, Pickup, { kind, radius: 0.95, age: 0 })
  if (gfx) world.add(e, Renderable, { objectId: gfx.addPickup(kind) })
  return e
}

function collect(world: World): void {
  const events = world.resource(EventsResource)
  const pickups = world.query([Pickup, Transform]).entities()
  if (pickups.length === 0) return

  for (const player of world.query([PlayerTag, Transform, Actor]).entities()) {
    if (world.has(player, Falling)) continue
    const tr = world.read(player, Transform)

    for (const p of pickups) {
      if (!world.isAlive(p)) continue
      const pt = world.read(p, Transform)
      const pickup = world.read(p, Pickup)
      if (Math.hypot(pt.x - tr.x, pt.z - tr.z) > pickup.radius + 0.7) continue

      const buff = world.add(player, Buff)
      if (pickup.kind === 'shield') buff.shield = SHIELD_DURATION
      else buff.haste = HASTE_DURATION

      events.emit(Events.Pickup, {
        entity: player,
        player: world.read(player, PlayerTag).player,
        kind: pickup.kind,
        x: pt.x,
        z: pt.z,
      })
      world.destroyEntity(p)
    }
  }
}
